import { useEffect, useState } from 'react';
import {
  Drawer,
  FormField,
  NumberInput,
  Select,
  Textarea,
  DrawerForm,
  DrawerFormActions,
} from '../../components/index.js';

const ADJUSTMENT_TYPES = [
  { value: 'manualCorrection', label: 'Manual correction' },
  { value: 'riskAdjustedForecast', label: 'Risk-adjusted forecast' },
  { value: 'scopeChange', label: 'Scope change' },
  { value: 'fundingShift', label: 'Funding shift' },
  { value: 'realignment', label: 'Strategic realignment' },
];

const FUNDING_SOURCES = [
  { value: 'O&M', label: 'O&M — Operation & Maintenance' },
  { value: 'RDT&E', label: 'RDT&E — Research, Development, Test & Eval' },
  { value: 'PROC', label: 'PROC — Procurement' },
  { value: 'MILCON', label: 'MILCON — Military Construction' },
];

const DEFAULTS = (defaults = {}) => ({
  projectId: defaults.projectId || '',
  adjustmentType: 'manualCorrection',
  deltaUsdM: '',
  fundingSource: 'O&M',
  reason: '',
  recommendedAction: '',
});

export function AdjustForecastDrawer({
  open,
  onClose,
  onSubmit,
  submitting,
  projects = [],
  defaults = {},
}) {
  const [draft, setDraft] = useState(DEFAULTS(defaults));
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (open) {
      setDraft(DEFAULTS(defaults));
      setErrors({});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  function update(field, value) {
    setDraft((d) => ({ ...d, [field]: value }));
    if (errors[field]) setErrors((e) => ({ ...e, [field]: null }));
  }

  function validate() {
    const next = {};
    if (!draft.projectId) next.projectId = 'Pick the project this adjustment applies to.';
    if (draft.deltaUsdM === '' || draft.deltaUsdM == null)
      next.deltaUsdM = 'Delta is required ($M, signed).';
    if (!draft.reason.trim()) next.reason = 'Reason is required.';
    setErrors(next);
    return Object.keys(next).length === 0;
  }

  function handleSubmit() {
    if (!validate()) return;
    onSubmit?.({
      projectId: draft.projectId,
      adjustmentType: draft.adjustmentType,
      deltaUsdM: Number(draft.deltaUsdM),
      fundingSource: draft.fundingSource,
      reason: draft.reason.trim(),
      recommendedAction: draft.recommendedAction.trim() || null,
    });
  }

  return (
    <Drawer open={open} onClose={onClose} title="Adjust forecast" width={540}>
      <DrawerForm onSubmit={handleSubmit} submitting={submitting}>
        <FormField label="Project" htmlFor="adj-project" required error={errors.projectId}>
          <Select
            id="adj-project"
            value={draft.projectId}
            onChange={(v) => update('projectId', v)}
            options={[
              { value: '', label: 'Select project…', disabled: true },
              ...projects.map((p) => ({ value: p.id, label: `${p.name} (${p.id})` })),
            ]}
          />
        </FormField>

        <FormField label="Adjustment type" htmlFor="adj-type">
          <Select
            id="adj-type"
            value={draft.adjustmentType}
            onChange={(v) => update('adjustmentType', v)}
            options={ADJUSTMENT_TYPES}
          />
        </FormField>

        <FormField
          label="Delta in $M (signed)"
          htmlFor="adj-delta"
          required
          error={errors.deltaUsdM}
          hint="Use a negative value for cuts; positive for scope adds."
        >
          <NumberInput
            id="adj-delta"
            value={draft.deltaUsdM}
            onChange={(v) => update('deltaUsdM', v)}
            step="0.1"
            suffix="$M"
          />
        </FormField>

        <FormField label="Funding source" htmlFor="adj-fund">
          <Select
            id="adj-fund"
            value={draft.fundingSource}
            onChange={(v) => update('fundingSource', v)}
            options={FUNDING_SOURCES}
          />
        </FormField>

        <FormField label="Reason" htmlFor="adj-reason" required error={errors.reason}>
          <Textarea
            id="adj-reason"
            value={draft.reason}
            onChange={(v) => update('reason', v)}
            rows={3}
            placeholder="What's driving this adjustment? Reference the request, ticket, or memo."
          />
        </FormField>

        <FormField label="Recommended action" htmlFor="adj-action">
          <Textarea
            id="adj-action"
            value={draft.recommendedAction}
            onChange={(v) => update('recommendedAction', v)}
            rows={2}
            placeholder="Optional — what should the portfolio review consider?"
          />
        </FormField>

        <DrawerFormActions
          onCancel={onClose}
          submitLabel="Submit adjustment"
          submitting={submitting}
        />
      </DrawerForm>
    </Drawer>
  );
}
